import { sameAddress } from "../../src/links/address.ts";
import type { Item, Link, OwnLinkField } from "../../src/web/types.ts";

// An item's links, the same wherever it shows (its card on the map, an option on Ideas, a friend's
// pick): its site, its booking page, a call, an email, its other and social links, and saved posts and
// videos about it by their previews. Web links open in a new tab. On a place's card (`trip` given), a saved post or
// video has its own Remove (DESIGN.md, "What the web adds, it can take away"): it deletes the link, from every place
// it's about, as an AI's delete does.
//
// Each address once (DESIGN.md, "A card reads in sections"): a website that is also among its links, written with or
// without "www.", a slash at its end or "http", is one pill; and two pages of one site say which is which, the
// second by its path ("karaksahotels.com/en/shin_osaka/room/room11"), so they never read as the same pill twice (seen
// on the founder's karaksa Shin-Osaka, 2026-10-26: its site and a room's page both said "karaksahotels.com").

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};
const host = (url: string) => {
  const name = hostOf(url);
  return /instagram\.com$/.test(name)
    ? "Instagram"
    : /google\.[a-z.]+$|goo\.gl$|maps\.app\.goo\.gl$/.test(name)
      ? "Google Maps"
      : name;
};
// The site and its page, as said when a pill's host alone is taken by another: "karaksahotels.com/en/shin_osaka".
const hostAndPath = (url: string) => {
  try {
    const u = new URL(url);
    return `${hostOf(url)}${u.pathname.replace(/\/+$/, "")}`;
  } catch {
    return url;
  }
};

// One address however it's written (src/links/address.ts).
export { sameAddress };

// One of an item's links: `link` when it's a saved post or video (a link record), not one of the item's own.
interface ItemLink {
  url: string;
  label: string;
  thumbnail?: string;
  link?: Link;
}

// What a card shows elsewhere leaves its pill out: its phone, which At a glance calls, and its booking page, under
// Booking (DESIGN.md, "A card reads in sections").
export interface LeaveOut {
  phone?: boolean;
  booking?: boolean;
}

// One address of an item's, as one pill shows it, and everywhere it's kept: the fields of its own that hold it (its
// site, its booking page, its email, its other links, its social pages) and the saved posts about it at that address.
interface Address {
  url: string;
  label: string;
  thumbnail?: string;
  link?: Link;
  own: OwnLinkField[];
  saved: Link[];
  // Where it was first found, and its place there: what it's known by as it changes.
  first: string;
}

function addresses(item: Item, saved: Link[], leaveOut: LeaveOut = {}): Address[] {
  const contact = item.contact;
  const out: Address[] = [];
  const at = new Map<string, Address>();
  const count = new Map<string, number>();
  const add = (
    url: string | undefined,
    label: string,
    from: { own: OwnLinkField } | { saved: Link } | { phone: true },
    thumbnail?: string,
  ) => {
    if (!url) return;
    const kind = "own" in from ? from.own : "saved" in from ? from.saved.id : "phone";
    const n = count.get(kind) ?? 0;
    count.set(kind, n + 1);
    const found = at.get(sameAddress(url));
    if (found) {
      if ("own" in from && !found.own.includes(from.own)) found.own.push(from.own);
      if ("saved" in from) found.saved.push(from.saved);
      return;
    }
    // A web page whose label is its host, when another pill already says that host: its path too.
    const taken = /^https?:/.test(url) && label === host(url) && out.some((l) => l.label === label);
    const address: Address = {
      url,
      label: taken ? hostAndPath(url) : label,
      ...(thumbnail ? { thumbnail } : {}),
      ...("saved" in from ? { link: from.saved } : {}),
      own: "own" in from ? [from.own] : [],
      saved: "saved" in from ? [from.saved] : [],
      first: "saved" in from ? from.saved.id : `${kind}:${n}`,
    };
    at.set(sameAddress(url), address);
    out.push(address);
  };
  add(contact?.website, host(contact?.website ?? ""), { own: "website" });
  // Booked, the page is where the booking is kept; before, where it's made.
  add(item.booking?.url, bookingLabel(item), { own: "booking" });
  if (contact?.phone && !leaveOut.phone)
    add(`tel:${contact.phone.replace(/[^\d+]/g, "")}`, `Call ${contact.phone}`, { phone: true });
  if (contact?.email) add(`mailto:${contact.email}`, `Email ${contact.email}`, { own: "email" });
  for (const url of item.links ?? []) add(url, host(url), { own: "links" });
  for (const url of contact?.social ?? []) add(url, host(url), { own: "social" });
  for (const l of saved.filter((s) => s.items.includes(item.id)))
    add(l.url, savedLabel(l), { saved: l }, l.preview?.ok ? l.preview.thumbnail : undefined);
  // Its booking page, under Booking where it's left out: no pill here, nor its other links' with its address (its
  // website, found first, keeps its own).
  return leaveOut.booking ? out.filter((a) => !a.first.startsWith("booking:")) : out;
}

export function itemLinks(item: Item, saved: Link[], leaveOut: LeaveOut = {}): ItemLink[] {
  return addresses(item, saved, leaveOut).map(({ url, label, thumbnail, link }) => ({
    url,
    label,
    ...(thumbnail ? { thumbnail } : {}),
    ...(link ? { link } : {}),
  }));
}

// A link's words, by its preview where it has one ("YouTube: Ramen crawl"), else its note, else its site.
export function savedLabel(l: Link): string {
  const p = l.preview?.ok ? l.preview : null;
  return p?.title ? `${p.site ? `${p.site}: ` : ""}${p.title}` : (l.note ?? host(l.url));
}

// A card's links as Edit lists them (features/links.md, "Add, edit and remove every link on the web"): one row for
// each pill, its booking page's too, with everywhere its address is kept: one pill is one link, so Edit changes it,
// and Remove takes it off, in every field of the card that holds it and every saved post about it at that address
// (the lead's rule, 2026-10-30: the same address often sits in two of a card's fields, its site and its links, its
// booking page and its links). What it is says where it's kept ("Website, link", "Saved by Mei"). Its phone isn't
// among them (At a glance calls it; the AI keeps it).
export interface LinkRow {
  key: string;
  url: string;
  label: string;
  what: string;
  thumbnail?: string;
  own: OwnLinkField[];
  saved: Link[];
}

const KIND: Record<OwnLinkField, string> = {
  website: "website",
  booking: "booking page",
  email: "email",
  links: "link",
  social: "social page",
};

export function linkRows(item: Item, saved: Link[], you?: string): LinkRow[] {
  return addresses(item, saved, { phone: true }).map((a) => {
    const by = a.saved.map((l) => `saved by ${l.created.by.id === you ? "you" : l.created.by.name}`);
    const what = [...a.own.map((f) => KIND[f]), ...new Set(by)].join(", ");
    return {
      key: a.first,
      url: a.url,
      label: a.label,
      what: what.charAt(0).toUpperCase() + what.slice(1),
      ...(a.thumbnail ? { thumbnail: a.thumbnail } : {}),
      own: a.own,
      saved: a.saved,
    };
  });
}

// An address as Edit shows it to change: an email without its "mailto:", a web page whole.
export function addressOf(url: string): string {
  return url.replace(/^mailto:/i, "");
}

// Its booking page's words: booked, where the booking is kept ("Booking on Expedia"); before, where it's made.
export function bookingLabel(item: Item): string {
  const booked = item.status === "booked" || item.status === "done";
  const provider = item.booking?.provider;
  return provider ? `${booked ? "Booking on" : "Book on"} ${provider}` : booked ? "The booking" : "Booking page";
}
